import { useEffect, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import moment from 'moment';
import DocumentUploadForm from './DocumentUploadForm';

const GENERAL_MAIN_CATEGORY_NAME = 'General Documents';

export default function Documents({ client, onRefresh }) {
  const [documents, setDocuments] = useState([]);
  const [categoryList, setCategoryList] = useState([]);

  useEffect(() => {
    if (client?.documents) setDocuments(client.documents);
  }, [client]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const data = await response.json();
      if (data?.body?.categories) {
        setCategoryList(data.body.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getDocumentName = (doc) => doc.docName || doc.name || 'Document';

  const handleOpenDocument = (doc) => {
    if (doc?.location) {
      window.open(doc.location, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadDocument = async (doc) => {
    if (!doc?.location) return;
    try {
      const response = await fetch(doc.location);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getDocumentName(doc);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      window.open(doc.location, '_blank', 'noopener,noreferrer');
    }
  };

  const handleUploadSuccess = (updatedApplication) => {
    if (updatedApplication?.documents) {
      setDocuments(updatedApplication.documents);
    }
    if (onRefresh) onRefresh(updatedApplication);
  };

  const visibleCategories = categoryList.filter((cat) => {
    const directDocuments = documents?.filter((doc) => {
      const docCat = doc.documentCategory?._id || doc.documentCategory;
      const docSub = doc.documentSubcategory?._id || doc.documentSubcategory;
      return docCat == cat._id && !docSub;
    });
    const hasSubcategoryDocuments = (cat.children || []).some((sub) => {
      const subDocs = documents?.filter((doc) => {
        const docCat = doc.documentCategory?._id || doc.documentCategory;
        const docSub = doc.documentSubcategory?._id || doc.documentSubcategory;
        return docCat == cat._id && docSub == sub._id;
      });
      return subDocs.length > 0;
    });
    const isGeneralCategory = cat.name === GENERAL_MAIN_CATEGORY_NAME;
    return isGeneralCategory || directDocuments.length > 0 || hasSubcategoryDocuments;
  });

  return (
    <div className="space-y-6">
      {client?.status === 'Finalized' && (
        <DocumentUploadForm
          client={client}
          categoryList={categoryList}
          setCategoryList={setCategoryList}
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {documents.length === 0 && <p className="text-gray-500 italic">No documents available.</p>}

      {visibleCategories.map((cat) => {
        const isGeneralCategory = cat.name === GENERAL_MAIN_CATEGORY_NAME;
        const directDocuments = documents?.filter((doc) => {
          const docCat = doc.documentCategory?._id || doc.documentCategory;
          const docSub = doc.documentSubcategory?._id || doc.documentSubcategory;
          return docCat == cat._id && !docSub;
        });

        return (
          <div key={cat._id} className="mb-8">
            <h3 className="text-gray-800 font-semibold text-lg">{cat.name}</h3>
            <hr className="my-3 border-gray-300" />
            <div className="space-y-4">
              {directDocuments && directDocuments.length > 0 && (
                <div className="space-y-3">
                  {directDocuments.map((doc) => (
                    <div key={doc._id} className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => handleOpenDocument(doc)}
                        className="text-[#005787] hover:underline text-md flex items-center gap-2"
                        title="Open document"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {getDocumentName(doc)} {doc.createdAt ? `(${moment(doc.createdAt).format('DD MMM YYYY')})` : ''}
                      </button>
                      <div className="flex items-center gap-3 text-[#005787]">
                        <button
                          type="button"
                          onClick={() => handleDownloadDocument(doc)}
                          className="hover:text-[#023f5d]"
                          title="Download document"
                        >
                          <FiDownload size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {cat.children && cat.children.length > 0 && cat.children.map((sub) => {
                const subDocs = documents?.filter((doc) => {
                  const docCat = doc.documentCategory?._id || doc.documentCategory;
                  const docSub = doc.documentSubcategory?._id || doc.documentSubcategory;
                  return docCat == cat._id && docSub == sub._id;
                });
                if (!isGeneralCategory && subDocs.length === 0) return null;

                return (
                  <div key={sub._id} className="pl-6 border-l-2 border-gray-100 mt-4">
                    <h4 className="text-gray-600 font-medium text-sm mb-2">{sub.name}</h4>
                    <div className="space-y-2">
                      {subDocs && subDocs.length > 0 ? (
                        subDocs.map((doc) => (
                          <div key={doc._id} className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => handleOpenDocument(doc)}
                              className="text-[#005787] hover:underline text-sm flex items-center gap-2"
                              title="Open document"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              {getDocumentName(doc)} {doc.createdAt ? `(${moment(doc.createdAt).format('DD MMM YYYY')})` : ''}
                            </button>
                            <div className="flex items-center gap-3 text-[#005787]">
                              <button
                                type="button"
                                onClick={() => handleDownloadDocument(doc)}
                                className="hover:text-[#023f5d]"
                                title="Download document"
                              >
                                <FiDownload size={15} />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 italic">No documents</p>
                      )}
                    </div>
                  </div>
                );
              })}

              {(!directDocuments || directDocuments.length === 0) && (!cat.children || cat.children.length === 0) && (
                <p className="text-sm text-gray-400 italic">No documents</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
